import React, { memo, useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, Platform, type TextStyle } from 'react-native';
import { BottomSheetPopup } from '../../components/common/BottomSheetPopup';
import { Clickable, PRESS_PRESETS } from '../../components/common/Clickable';
import { SolidCtaClickable } from '../../components/common/SolidCtaClickable';
import { SvgIcon } from '../../components/common/SvgIcon';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';
import { androidFlatSurfaceStyle, androidScaleFont, androidScaleSize } from '../../shared/theme/androidUiParity';
import { useTranslation } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/useRtl';
import { DASH_STUDIO_RADIUS } from '../../shared/theme/studio/dashboardConstants';
import { buildV3AddressDetailLines, type AddressLike } from '../../data/domains/addresses';
import type { Address } from '../../data/domains/addresses';

const ANDROID_TEXT_COMPACT: TextStyle = Platform.OS === 'android' ? { includeFontPadding: false } : {};
const ICON_SIZE = androidScaleSize(40);

type Props = {
  visible: boolean;
  onClose: () => void;
  addresses: Address[];
  selected_address_id?: number | string | null;
  on_select: (address: Address) => void;
  on_add_new: () => void;
};

function create_styles(c: ThemeColors) {
  const selected_bg = (hexToRgba(c.primary, 0.08) as string) || c.primaryUltra;
  const icon_bg = (hexToRgba(c.primary, 0.1) as string) || c.primaryUltra;

  return StyleSheet.create({
    sheet: {
      backgroundColor: c.surface,
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
      maxHeight: '80%',
    },
    list: {
      flexGrow: 1,
    },
    list_content: {
      paddingHorizontal: appTheme.spacing.lg,
      paddingTop: appTheme.spacing.xs,
      paddingBottom: appTheme.spacing.lg,
      alignSelf: 'stretch',
      width: '100%',
    },
    card: {
      borderRadius: DASH_STUDIO_RADIUS,
      backgroundColor: c.backgroundElevated,
      marginBottom: appTheme.spacing.sm,
      overflow: 'hidden',
      alignSelf: 'stretch',
      width: '100%',
      ...appTheme.shadows.xs,
      shadowColor: c.shadowColor,
    },
    card_selected: {
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.primary,
      backgroundColor: selected_bg,
    },
    card_inner: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      padding: appTheme.spacing.md,
      gap: appTheme.spacing.md,
      minWidth: 0,
    },
    icon_wrap: {
      width: ICON_SIZE,
      height: ICON_SIZE,
      borderRadius: 12,
      backgroundColor: icon_bg,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      ...androidFlatSurfaceStyle(),
    },
    content: {
      flex: 1,
      minWidth: 0,
      gap: 4,
    },
    title_row: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 8,
      minWidth: 0,
    },
    name: {
      fontSize: androidScaleFont(16),
      fontWeight: '700',
      color: c.text,
      flexShrink: 1,
      ...ANDROID_TEXT_COMPACT,
    },
    default_badge: {
      backgroundColor: c.primary,
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: appTheme.borderRadius.pill,
    },
    default_badge_text: {
      fontSize: androidScaleFont(10),
      fontWeight: '700',
      color: c.onPrimary ?? c.white,
      letterSpacing: 0.3,
      textTransform: 'uppercase',
      ...ANDROID_TEXT_COMPACT,
    },
    detail_line: {
      fontSize: androidScaleFont(13),
      fontWeight: '500',
      lineHeight: androidScaleFont(18),
      color: c.textSecondary,
      ...ANDROID_TEXT_COMPACT,
    },
    phone_row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      marginTop: 2,
    },
    phone_text: {
      fontSize: androidScaleFont(12),
      fontWeight: '600',
      color: c.textLight,
      ...ANDROID_TEXT_COMPACT,
    },
    check_wrap: {
      width: 28,
      height: 28,
      borderRadius: 14,
      backgroundColor: c.primary,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      marginTop: 6,
    },
    add_cta: {
      marginTop: appTheme.spacing.xs,
    },
  });
}

const AddressPickerCard = memo(function AddressPickerCard({
  address,
  is_selected,
  on_press,
  styles,
  theme_colors,
}: {
  address: Address;
  is_selected: boolean;
  on_press: () => void;
  styles: ReturnType<typeof create_styles>;
  theme_colors: ThemeColors;
}) {
  const { t } = useTranslation();
  const rtl = useRtl();
  const detail_lines = useMemo(
    () => buildV3AddressDetailLines(address as unknown as AddressLike, t),
    [address, t],
  );
  const phone = address.phone != null && String(address.phone).trim() !== '' ? String(address.phone) : '';

  return (
    <Clickable
      onPress={on_press}
      pressScale={PRESS_PRESETS.listRow.scale}
      haptics
      accessibilityRole="button"
      accessibilityState={{ selected: is_selected }}>
      <View style={[styles.card, is_selected && styles.card_selected]}>
        <View style={styles.card_inner}>
          <View style={styles.icon_wrap}>
            <SvgIcon name="map" size={20} color={theme_colors.primary} />
          </View>
          <View style={styles.content}>
            <View style={styles.title_row}>
              <Text
                style={[styles.name, rtl.textStyle({ align: 'start', writing: 'locale', stretch: false })]}
                numberOfLines={1}>
                {address.name || t('address.addressFallback')}
              </Text>
              {address.is_default ? (
                <View style={styles.default_badge}>
                  <Text style={styles.default_badge_text}>{t('address.default')}</Text>
                </View>
              ) : null}
            </View>
            {detail_lines.map((line, index) => (
              <Text key={`${address.id ?? 'addr'}-${index}`} style={[styles.detail_line, rtl.textFollowLocale]} numberOfLines={2}>
                {line}
              </Text>
            ))}
            {phone ? (
              <View style={styles.phone_row}>
                <SvgIcon name="call" size={14} color={theme_colors.textLight} />
                <Text
                  style={[styles.phone_text, rtl.textStyle({ align: 'start', writing: 'locale', stretch: false })]}
                  numberOfLines={1}>
                  {phone}
                </Text>
              </View>
            ) : null}
          </View>
          {is_selected ? (
            <View style={styles.check_wrap}>
              <SvgIcon name="check" size={16} color={theme_colors.onPrimary ?? theme_colors.white} />
            </View>
          ) : null}
        </View>
      </View>
    </Clickable>
  );
});

export const CheckoutAddressPickerSheet = memo(function CheckoutAddressPickerSheet({
  visible,
  onClose,
  addresses,
  selected_address_id,
  on_select,
  on_add_new,
}: Props) {
  const { t } = useTranslation();
  const theme_colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => create_styles(theme_colors), [theme_colors]);

  return (
    <BottomSheetPopup
      visible={visible}
      onClose={onClose}
      title={t('checkout.selectAddress')}
      animationType="slide"
      showDragHandle
      sheetStyle={styles.sheet}>
      <ScrollView
        style={styles.list}
        contentContainerStyle={styles.list_content}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        {addresses.map(address => {
          const is_selected = selected_address_id != null && String(selected_address_id) === String(address.id);
          return (
            <AddressPickerCard
              key={String(address.id)}
              address={address}
              is_selected={is_selected}
              on_press={() => on_select(address)}
              styles={styles}
              theme_colors={theme_colors}
            />
          );
        })}
        <View style={styles.add_cta}>
          <SolidCtaClickable
            label={t('address.addNew')}
            onPress={on_add_new}
            variant="primary"
            size="compact"
            accessibilityLabel={t('address.addNew')}
          />
        </View>
      </ScrollView>
    </BottomSheetPopup>
  );
});

export default CheckoutAddressPickerSheet;
